// src/pages/AddCardsPage.tsx
// Página para visualizar todos os subdecks da plataforma e adicionar cards

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X, FolderOpen, Inbox } from 'lucide-react';
import './AddCardsPage.css';
import Toast from '../components/Toast';

interface Subdeck {
    _id: string;
    name: string;
    description: string;
    cardCount: number;
    deckId: string;
    deckName: string;
}

const AddCardsPage: React.FC = () => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();

    const [subdecks, setSubdecks] = useState<Subdeck[]>([]);
    const [filteredSubdecks, setFilteredSubdecks] = useState<Subdeck[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const loadAllSubdecks = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            if (!token) {
                console.error('Token não encontrado');
                setToast({ message: 'Você precisa fazer login novamente', type: 'error' });
                setTimeout(() => navigate('/login'), 2000);
                return;
            }

            const response = await fetch('/api/decks/all-subdecks', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();

                if (Array.isArray(data) && data.length > 0) {
                    setSubdecks(data);
                    setFilteredSubdecks(data);
                } else {
                    setSubdecks([]);
                    setFilteredSubdecks([]);
                }
            } else {
                const errorData = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
                setToast({ message: 'Erro ao carregar subdecks: ' + errorData.message, type: 'error' });
                setSubdecks([]);
                setFilteredSubdecks([]);
            }
        } catch (error) {
            console.error('Erro ao carregar subdecks:', error);
            setToast({ message: 'Erro de conexão. Verifique se o servidor está rodando.', type: 'error' });
            setSubdecks([]);
            setFilteredSubdecks([]);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadAllSubdecks();
    }, [loadAllSubdecks]);

    useEffect(() => {
        // Filtrar subdecks baseado na pesquisa
        if (searchQuery.trim() === '') {
            setFilteredSubdecks(subdecks);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = subdecks.filter(subdeck =>
                subdeck.name.toLowerCase().includes(query) ||
                subdeck.deckName.toLowerCase().includes(query) ||
                subdeck.description.toLowerCase().includes(query)
            );
            setFilteredSubdecks(filtered);
        }
    }, [searchQuery, subdecks]);

    const handleViewSubdeck = (subdeck: Subdeck) => {
        const savedContext = sessionStorage.getItem('subdecksContext');
        const context = savedContext ? JSON.parse(savedContext) : {};

        // Navegar para ViewPage com parâmetros de adicionar + contexto
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(`/view/${subdeck.deckId}/${subdeck._id}?addTo=${deckId}&subdeck=${subdeckId}`, {
            state: context
        });
    };

    if (loading) {
        return (
            <div className="add-cards-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Carregando subdecks</p>
                </div>
            </div>
        );
    }

    return (
        <div className="add-cards-page">
            {/* Header */}
            <div className="addcd-page-header">
                <button
                    className="addcd-btn-back"
                    onClick={() => {
                        const savedContext = sessionStorage.getItem('subdecksContext');
                        const context = savedContext ? JSON.parse(savedContext) : {};

                        navigate(`/custom-decks/${deckId}/subdecks`, {
                            state: context
                        });
                    }}
                >
                    <ArrowLeft size={18} />
                    Voltar
                </button>
                <div className="addcd-header-content">
                    <h1 className="addcd-title">Adicionar Cards da Plataforma</h1>
                    <p className="addcd-subtitle">
                        Selecione um subdeck para visualizar e adicionar flashcards ao seu deck personalizado
                    </p>
                </div>
            </div>

            {/* Barra de Pesquisa */}
            <div className="addcd-search-container">
                <div className="addcd-search-wrapper">
                    <Search
                        size={18}
                        className="addcd-search-icon"
                    />
                    <input
                        type="text"
                        className="addcd-search-input"
                        placeholder="Pesquisar por nome do subdeck ou deck"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    {searchQuery && (
                        <button
                            className="addcd-btn-clear-search"
                            onClick={() => setSearchQuery('')}
                            aria-label="Limpar pesquisa"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
                {searchQuery && (
                    <div className="addcd-search-results-count">
                        <span>
                            {filteredSubdecks.length} resultado{filteredSubdecks.length !== 1 ? 's' : ''} encontrado{filteredSubdecks.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                )}
            </div>

            {/* Grid de Subdecks */}
            <div className="subdecks-container">
                {filteredSubdecks.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">
                            {searchQuery ? (
                                <Search size={64} style={{ opacity: 0.3 }} />
                            ) : (
                                <Inbox size={64} style={{ opacity: 0.3 }} />
                            )}
                        </div>
                        <h2>
                            {searchQuery
                                ? 'Nenhum subdeck encontrado'
                                : 'Nenhum subdeck disponível'
                            }
                        </h2>
                        <p>
                            {searchQuery
                                ? 'Tente usar outros termos de busca'
                                : 'Não há subdecks cadastrados na plataforma ainda'
                            }
                        </p>
                        {searchQuery && (
                            <button
                                className="btn-clear-search-large"
                                onClick={() => setSearchQuery('')}
                            >
                                Limpar Pesquisa
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="subdecks-grid">
                        {filteredSubdecks.map((subdeck) => (
                            <div
                                key={subdeck._id}
                                className="subdeck-card"
                                onClick={() => handleViewSubdeck(subdeck)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleViewSubdeck(subdeck);
                                    }
                                }}
                            >
                                <div className="subdeck-card-header">
                                    <h3>{subdeck.name}</h3>
                                    <span className="card-count-badge">
                                        {subdeck.cardCount} card{subdeck.cardCount !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                <p className="subdeck-deck-name">
                                    <FolderOpen size={16} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                                    {subdeck.deckName}
                                </p>

                                {subdeck.description && (
                                    <p className="subdeck-description">
                                        {subdeck.description}
                                    </p>
                                )}

                                <button
                                    className="btn-view-subdeck"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewSubdeck(subdeck);
                                    }}
                                >
                                    Visualizar Cards
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer Info */}
            {filteredSubdecks.length > 0 && (
                <div className="page-footer">
                    <p>
                        <strong>Dica:</strong> Clique em Visualizar Cards para ver todos os flashcards do subdeck.
                        Você poderá adicionar cards individuais clicando no botão de adicionar em cada card.
                    </p>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AddCardsPage;